'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useReactionSound } from '@/hooks/useReactionSound';

export type ReactionType = '👏' | '👍' | '❤️' | '😂' | '😮';

export interface Reaction {
  id: string;
  type: ReactionType;
  participantId: string;
  participantName: string;
  timestamp: number;
}

interface ReactionsContextType {
  reactions: Reaction[];
  addReaction: (reaction: Reaction) => void;
  clearReactions: () => void;
}

const ReactionsContext = createContext<ReactionsContextType | undefined>(undefined);

export function ReactionsProvider({ children }: { children: React.ReactNode }) {
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const { playSound } = useReactionSound(); // <--- 2. Inicialize o hook

  const addReaction = useCallback((reaction: Reaction) => {
    // <--- 3. Toca o som assim que a reação chega
    playSound(reaction.type);

    setReactions((prev) => {
      const now = Date.now();
      const filtered = prev.filter((r) => now - r.timestamp < 5000);
      return [...filtered, reaction];
    });
  }, [playSound]); // Adicione playSound nas dependências

  const clearReactions = useCallback(() => {
    setReactions([]);
  }, []);

  const value: ReactionsContextType = {
    reactions,
    addReaction,
    clearReactions,
  };

  return <ReactionsContext.Provider value={value}>{children}</ReactionsContext.Provider>;
}

export function useReactions() {
  const context = useContext(ReactionsContext);
  if (context === undefined) {
    throw new Error('useReactions must be used within a ReactionsProvider');
  }
  return context;
}